import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  WidthType,
  TextRun,
  BorderStyle,
  AlignmentType,
  VerticalAlign,
  PageBreak,
} from "docx";
import { saveAs } from "file-saver";
import {
  ThemeProvider,
  StyledEngineProvider,
  Card,
  createTheme,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#008752",
    },
  },
  backdrop: {
    zIndex: 9999999,
    color: "#000",
  },
});

const FONT_NAME = "Aptos Narrow";

const NORMALIZED_MENU_NAME = "menuname";

const getCellValue = (sheet, row, col) => {
  const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
  const cell = sheet[cellAddress];
  return cell ? String(cell.v ?? "") : "";
};

const normalizeText = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const isUsableTitle = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  return normalizeText(trimmed) !== "text";
};

const FULL_MENU_TITLE_REGEX = /^men(?:u|ü)\s*\d+\s*:/i;
const SHORT_MENU_LABEL_REGEX = /^men(?:u|ü)\s*\d+$/i;

const getTitleScore = (value) => {
  const text = String(value ?? "").trim();
  if (!isUsableTitle(text)) return -1;

  let score = 0;

  if (FULL_MENU_TITLE_REGEX.test(text)) score += 100;
  if (/men(?:u|ü)/i.test(text) && text.includes(":")) score += 40;
  if (text.length >= 25) score += 20;
  if (text.length >= 40) score += 10;
  if (SHORT_MENU_LABEL_REGEX.test(text)) score -= 40;

  return score;
};

const extractMenuTitle = (sheet, range) => {
  let bestTitle = "";
  let bestScore = -1;

  const considerTitle = (candidate) => {
    const value = String(candidate ?? "").trim();
    const score = getTitleScore(value);
    if (score > bestScore) {
      bestScore = score;
      bestTitle = value;
    }
  };

  // Prefer full explicit menu titles like "MENÜ 1: ..." wherever they appear.
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const current = getCellValue(sheet, r, c).trim();
      if (FULL_MENU_TITLE_REGEX.test(current)) {
        considerTitle(current);
      }
    }
  }

  // New format can carry "Menu Name" as a label/value pair, so scan for it first.
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const current = getCellValue(sheet, r, c).trim();
      if (!current) continue;

      const normalized = normalizeText(current);
      if (!normalized.includes(NORMALIZED_MENU_NAME)) continue;

      const inlineMatch = current.match(/menu\s*name\s*[:\-]\s*(.+)$/i);
      if (inlineMatch?.[1] && isUsableTitle(inlineMatch[1])) {
        considerTitle(inlineMatch[1]);
      }

      for (let nextCol = c + 1; nextCol <= range.e.c; nextCol++) {
        const candidate = getCellValue(sheet, r, nextCol).trim();
        if (isUsableTitle(candidate)) considerTitle(candidate);
      }

      const belowCandidate = getCellValue(sheet, r + 1, c).trim();
      if (isUsableTitle(belowCandidate)) considerTitle(belowCandidate);
    }
  }

  if (bestTitle) return bestTitle;

  const fallbackTitle = getCellValue(sheet, 0, 0).trim();
  return isUsableTitle(fallbackTitle) ? fallbackTitle : "";
};

function ExceltoWord() {
  const fileRef = useRef(null);
  const [filesData, setFilesData] = useState([]);
  const [fileCount, setFileCount] = useState(0);

  const handleChooseFiles = () => {
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const input = e.target;
    if (!input?.files?.length) return;

    const files = Array.from(input.files);
    const parsedFiles = [];

    for (const file of files) {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      if (!sheetName) continue;

      const sheet = workbook.Sheets[sheetName];
      const range = XLSX.utils.decode_range(sheet["!ref"]);

      const title = extractMenuTitle(sheet, range);

      const headers = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        headers.push(getCellValue(sheet, 2, c).trim());
      }

      const rows = [];
      for (let r = 3; r <= range.e.r; r++) {
        const row = [];
        for (let c = range.s.c; c <= range.e.c; c++) {
          row.push(getCellValue(sheet, r, c));
        }
        rows.push(row);
      }

      parsedFiles.push({
        title,
        headers,
        rows,
        fileName: file.name.replace(/\.[^/.]+$/, ""),
      });
    }

    setFilesData(parsedFiles);
    setFileCount(parsedFiles.length);
    input.value = "";
  };

  const makeCell = (value) =>
    new TableCell({
      verticalAlign: VerticalAlign.CENTER,
      margins: {
        right: 100,
      },
      children: [
        new Paragraph({
          children: [
            new TextRun({
              text: String(value ?? ""),
              font: FONT_NAME,
              size: 32,
            }),
          ],
        }),
      ],
    });

  const makeHeaderCell = (value) =>
    new TableCell({
      verticalAlign: VerticalAlign.CENTER,
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [
            new TextRun({
              text: String(value ?? ""),
              bold: true,
              font: FONT_NAME,
              size: 32,
            }),
          ],
        }),
      ],
    });

  const buildDoc = async (allFilesData, fileNameBase) => {
    const children = [];

    allFilesData.forEach((fileData, fileIndex) => {
      const { title, headers, rows } = fileData;
      const tableRows = [];

      tableRows.push(
        new TableRow({
          children: headers.map((header) => makeHeaderCell(header)),
        })
      );

      rows.forEach((row) => {
        tableRows.push(
          new TableRow({
            height: {
              value: 520,
              rule: "atLeast",
            },
            children: row.map((cell) => makeCell(cell)),
          })
        );
      });

      const table = new Table({
        width: {
          size: 100,
          type: WidthType.PERCENTAGE,
        },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 10, color: "000000" },
          bottom: { style: BorderStyle.SINGLE, size: 10, color: "000000" },
          left: { style: BorderStyle.SINGLE, size: 10, color: "000000" },
          right: { style: BorderStyle.SINGLE, size: 10, color: "000000" },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 10, color: "000000" },
          insideVertical: { style: BorderStyle.SINGLE, size: 10, color: "000000" },
        },
        rows: tableRows,
      });

      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
          children: [
            new TextRun({
              text: title || fileData.fileName || "Document Title",
              bold: true,
              font: FONT_NAME,
              size: 36,
            }),
          ],
        })
      );

      children.push(table);

      if (fileIndex < allFilesData.length - 1) {
        children.push(
          new Paragraph({
            children: [new PageBreak()],
          })
        );
      }
    });

    const doc = new Document({
      sections: [{ children }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${fileNameBase || "converted"}.docx`);
  };

  const handleGenerate = async () => {
    if (!filesData.length) {
      alert("Choose at least one Excel file first.");
      return;
    }
    await buildDoc(filesData, "converted");
  };

  return (
    <Grid
      className="mt-2"
      container
      spacing={2}
      alignItems="top"
      justifyContent="center"
      style={{ minHeight: "100vh" }}
    >
      <Grid item xs={12} md={10}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={defaultMaterialTheme}>
            <Card className="d-print-none">
              <article className="article">
                <div className="container-fluid no-breadcrumb chapter FormIntegration">
                  <Grid container justifyContent="space-between">
                    <Grid item xs={12} sm={12}>
                      <h2 className="article-title">
                        Lunch Order List Conversion
                      </h2>
                    </Grid>
                  </Grid>
                </div>
              </article>
            </Card>

            <Card className="mt-4 d-print-none">
              <article className="article">
                <div className="container-fluid no-breadcrumb chapter FormIntegration text-center">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    multiple
                    onChange={handleFile}
                    style={{ display: "none" }}
                  />

                  <div style={{ padding: 16 }}>
                    <Button
                      className="mt-4"
                      variant="outlined"
                      onClick={handleChooseFiles}
                      style={{ width: 500, height: 100 }}
                    >
                      Choose .xlsx Files
                    </Button>

                    {fileCount > 0 && (
                      <div style={{ marginTop: 8 }}>
                        {fileCount} file{fileCount !== 1 ? "s" : ""} selected
                      </div>
                    )}

                    <div style={{ marginTop: 16 }}>
                      <Button
                        className="mt-4"
                        variant="contained"
                        onClick={handleGenerate}
                        style={{ width: 200, height: 40 }}
                      >
                        Generate .docx
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            </Card>
          </ThemeProvider>
        </StyledEngineProvider>
      </Grid>
    </Grid>
  );
}

export default ExceltoWord;